import { LockClosedIcon, SparklesIcon } from '@heroicons/react/24/solid';
import { Link } from '@remix-run/react';
import cx from 'classnames';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime.js';
import { useTranslation } from 'react-i18next';

import { Image } from '~/components/Image';
import Text from '~/components/Text';

import type { Author } from '~/interfaces/author';
import type { Post } from '~/interfaces/post';
import type { Publication } from '~/interfaces/publication';

import PostBadge from './PostBadge';

interface PostProps {
  publication: Publication;
  post: Post;
  titleSize: '3xl' | '2xl' | 'xl' | 'lg' | 'md' | 'sm' | 'xs';
  descriptionSize: 'xl' | 'lg' | 'md' | 'sm' | 'xs';
  descriptionLines?: number;
  showImage: boolean;
  shouldEagerLoad?: boolean;
  orientation?: 'vertical' | 'horizontal';
  variant?: 'news' | 'default';
  isFeatured?: boolean;
  renderAuthors?: boolean;
  className?: string;
  theme?: 'subscribe' | 'content' | 'navs' | 'default' | 'primary';
}

dayjs.extend(relativeTime);

const PostCard = ({
  publication,
  post,
  titleSize,
  descriptionSize,
  showImage,
  shouldEagerLoad = false,
  orientation = 'vertical',
  variant = 'default',
  descriptionLines,
  isFeatured = false,
  renderAuthors = true,
  theme = 'content',
  className,
}: PostProps) => {
  const { t } = useTranslation();

  return (
    <Link
      to={`/p/${post.slug}`}
      prefetch="none"
      className={cx(
        'group relative flex rounded-wt',
        orientation === 'vertical'
          ? ''
          : 'grid w-full grid-cols-1 transition-all hover:bg-opacity-25 sm:my-6 sm:grid-cols-2',
        orientation === 'vertical' ? 'flex-col' : 'flex-row',
        !isFeatured && 'mb-6 gap-y-0 sm:mb-0',
        className,
        publication.web_theme.content_theme === 'light' ? 'hover:bg-slate-100/50' : '',
        publication.web_theme.content_theme === 'dark' ? 'hover:bg-white/5' : '',
        publication.web_theme.content_theme === 'secondary' ? 'hover:bg-white/20' : '',
      )}
    >
      {/* Featured Flag */}
      {isFeatured && publication.web_template.enable_featured_post_badges && (
        <div className="mb-2">
          <PostBadge
            theme={publication.web_theme.content_theme}
            text={t('posts.featured')}
            icon={<SparklesIcon className="z-10 h-3.5 w-3.5" />}
          />
        </div>
      )}

      {/* Image */}
      {showImage && (
        <div
          className={cx(
            'z-10 col-span-1 w-full overflow-hidden rounded-wt border',
            publication.web_theme.content_theme === 'light' ? 'bg-slate-100/50' : '',
            publication.web_theme.content_theme === 'dark' ? 'bg-white/5' : '',
            publication.web_theme.content_theme === 'secondary' ? 'bg-white/20' : '',
          )}
        >
          <Image
            src={post.image_url}
            alt={post.web_title}
            maxWidth={800}
            loading={shouldEagerLoad ? 'eager' : 'lazy'}
            aspectRatio="social"
          />
        </div>
      )}

      <div className="z-10 col-span-1 p-4">
        {/* Premium Tag */}
        {post.audience === 'premium' && (
          <PostBadge
            theme={publication.web_theme.content_theme}
            text={t('posts.premium')}
            icon={<LockClosedIcon className="z-10 h-3.5 w-3.5" />}
          />
        )}

        {/* Title */}
        <Text
          as="h2"
          size={titleSize}
          theme={theme}
          weight="bold"
          className={cx(
            'line-clamp-2 text-wt-text-on-background',
            variant === 'news' ? 'wt-header-font' : 'wt-header-font',
            variant === 'news' ? 'group-hover:underline' : '',
          )}
        >
          {post?.web_title}
        </Text>

        {/* Description */}
        <Text
          size={descriptionSize}
          theme={theme}
          className={cx(
            'font-regular mb-2 text-wt-text-on-background opacity-75',
            variant === 'news' ? 'wt-header-font' : 'wt-header-font',
            variant === 'news' ? 'group-hover:underline' : '',
            descriptionLines === 2 ? 'line-clamp-2' : 'line-clamp-4',
          )}
        >
          {post?.web_subtitle}
        </Text>

        {/* Authors */}
        {renderAuthors && (
          <Text
            size="xs"
            theme={theme}
            className={cx(
              'mb-4 text-wt-text-on-background no-underline opacity-75',
              variant === 'news' ? 'wt-header-font' : 'wt-header-font',
            )}
            weight="regular"
          >
            <span>{post.authors?.map((author: Author) => author.name).join(', ')}</span>
            {post.authors?.length >= 1 && <span> / </span>}
            <time dateTime={post.override_scheduled_at}>{/* {dayjs(post.override_scheduled_at).fromNow()} */}</time>
          </Text>
        )}
      </div>
    </Link>
  );
};

export default PostCard;
